import * as React from 'react';
import invariant from 'invariant';

import DialogContent from 'components/DialogContent';
import Link from 'components/Link';
import LinkDeviceInputBox from 'user/components/LinkDeviceInputBox';
import LinkDeviceVerificationCodeBox from 'user/components/LinkDeviceVerificationCodeBox';
import { SendType } from 'user/components/LinkDeviceVerificationCodeBox';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import DeviceActions from 'actions/DeviceActions';

// @ts-ignore
import { ConfirmableDeviceType } from 'entities/Device';

type Status = 'ask_device_address' | 'ask_code';

type Props = {
  /* returns auth token linked to the newly verified device*/
  onDone: (authToken?: string, deviceUid?: string) => void;
  /* optional - enable skipping */
  // only used in setupflow
  onSkip?: () => void;
  /* optional - show modal close button */
  closable?: boolean;
  /* optional - user-inputted address eg (4156152427) is required for instantResend and sendCodeOnInitialRender*/
  // only used in JoinConfirmSmsDevicePage and JoinConfirmDevicePage
  address?: string;
  /* optional - when true will resent code to mobile directly */
  // only used in JoinConfirmSmsDevicePage
  instantResend?: boolean;
  onRequestHide?: () => void;
  /* Skip updating device preferences:
   *
   * When adding a new device the backend
   * try to make smart choice about enabling the added device
   * and disabeling preixisting one. This skips it, the added
   * device will be disabled, and current devices will not change.
   */
  skipUpdateDevicePreferences?: boolean;
  title?: string;
  subtext?: string;
  deviceType: ConfirmableDeviceType;
  // for sign up flows, we already have the address and need to trigger
  // the code sign upon rendering the component, which means skipping straight
  // to the code input box. passing this flag in triggers sendCode
  sendCodeOnInitialRender?: boolean;
};

type State = {
  status: Status;
  sendType: SendType;
  submittedAddress: string;
  submitting: boolean;
};

const getStateFromProps = (props: Props): State => ({
  status: props.address ? 'ask_code' : 'ask_device_address',
  sendType: 'text',
  submittedAddress: props.address || '',
  submitting: false,
});

class LinkDevicePage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = getStateFromProps(props);
  }

  componentDidMount() {
    const { sendCodeOnInitialRender } = this.props;
    const { sendType, submittedAddress } = this.state;
    if (sendCodeOnInitialRender) {
      this.sendCode(submittedAddress, sendType);
    }
  }

  handleDeviceLinked = (authToken?: string, deviceUid?: string) => {
    this.props.onDone(authToken, deviceUid);
  };

  handleDeviceSubmit = async (submittedAddress: string) => {
    const sendResult = await this.sendCode(submittedAddress, this.state.sendType);

    if (sendResult && sendResult.address) {
      this.setState({
        status: 'ask_code',
        submittedAddress,
      });
    }
  };

  handleResend = (newSendType?: SendType) => {
    const { sendType } = this.state;
    if ((newSendType || this.props.instantResend) && this.props.deviceType === 'phoneNumber') {
      this.sendCode(this.state.submittedAddress, newSendType || sendType, true);
    } else {
      this.setState(getStateFromProps(this.props));
    }
  };

  sendCode = (address: string, sendType: SendType, isResend: boolean = false) => {
    const onSuccess = (response: any) => {
      if (isResend) {
        if (sendType === 'text') {
          ToastActions.showSuccessToast('Text sent');
        } else if (sendType === 'voice') {
          ToastActions.showSuccessToast('Calling phone...');
        } else {
          ToastActions.showSuccessToast('Code resent');
        }
      }
      this.setState({ submitting: false });

      return response;
    };

    this.setState({
      submitting: true,
      sendType,
    });
    return DeviceActions.outboundVerify(address, sendType === 'voice')
      .then(onSuccess, this.handleSendCodeError)
      .catch((err: any) => {
        console.debug(err);
        console.error(err.toString());
        ToastActions.showDefaultErrorToast();
      });
  };

  handleSendCodeError = (response: any) => {
    this.setState({ submitting: false });

    if (response && response.code === 'duplicate_device') {
      ToastActions.showErrorToast('This device is already linked to your account.');
    } else if (
      response &&
      (response.code === 'underage_account_conflict' || response.code === 'blocked_device')
    ) {
      ToastActions.showErrorToast('Please contact support for assistance adding this device.');
    } else if (
      (response && response.code === 'validation_error') ||
      response.code === 'invalid_address'
    ) {
      const message =
        (response.error.endpoint && response.error.endpoint[0]) ||
        `Please enter a valid ${this.deviceString()}.`;
      ToastActions.showErrorToast(message);
    } else {
      console.debug(response);
      console.error(response.toString());
      ToastActions.showDefaultErrorToast();
    }
  };

  renderAskConfirmationCode = () => {
    const { submittedAddress, sendType } = this.state;
    invariant(submittedAddress, 'LinkDevicePage must have a token in renderAskConfirmationCode');
    let footer = null;
    if (this.props.onSkip) {
      footer = (
        <span>
          or <Link onClick={this.props.onSkip}>skip this step</Link>
        </span>
      );
    }

    return (
      <LinkDeviceVerificationCodeBox
        skipUpdateDevicePreferences={this.props.skipUpdateDevicePreferences}
        onDone={this.handleDeviceLinked}
        submittedAddress={submittedAddress}
        onResend={this.handleResend}
        footer={footer}
        deviceType={this.props.deviceType}
        sendType={sendType}
      />
    );
  };

  deviceString = () => {
    return this.props.deviceType === 'email' ? 'email address' : 'phone number';
  };

  renderAskDeviceAddress = () => {
    let footer = null;
    if (this.props.onSkip) {
      footer = (
        <span>
          Don’t want to connect a {this.deviceString()}?{' '}
          <Link onClick={this.props.onSkip} data-test-selector="link-device-skip">
            Skip this step
          </Link>
        </span>
      );
    }
    return (
      <LinkDeviceInputBox
        closable={this.props.closable}
        footer={footer}
        onDone={this.handleDeviceSubmit}
        title={this.props.title}
        subtext={this.props.subtext}
        deviceType={this.props.deviceType}
        disabled={this.state.submitting}
      />
    );
  };

  renderBody = () => {
    switch (this.state.status) {
      case 'ask_device_address':
        return this.renderAskDeviceAddress();
      case 'ask_code':
        return this.renderAskConfirmationCode();
      default:
        return null;
    }
  };

  render() {
    return <DialogContent className="LinkDevicePage">{this.renderBody()}</DialogContent>;
  }
}

export default LinkDevicePage;
